import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { Bell, CheckCheck, X, Trash2 } from "lucide-react";

export function NotificationBell({ up = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  const removeOne = async (e, n) => {
    e.stopPropagation();
    await api.delete(`/notifications/${n.id}`).catch(() => {});
    load();
  };

  const clearAll = async () => {
    await api.delete("/notifications").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell-button" onClick={() => { setOpen(!open); if (!open) load(); }}
        className="relative rounded-sm border border-border p-2 transition-colors duration-200 hover:bg-accent" title="Notifications">
        <Bell className="h-4 w-4" />
        {data.unread_count > 0 && (
          <span data-testid="notification-badge"
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel"
          className={`absolute z-50 w-80 max-w-[85vw] rounded-sm border border-border bg-card shadow-xl ${up ? "bottom-full left-0 mb-2" : "right-0 mt-2"}`}>
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
            <span className="text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-3">
              <button data-testid="notifications-mark-all-read" onClick={markAll}
                className="flex items-center gap-1 text-xs text-primary hover:underline">
                <CheckCheck className="h-3.5 w-3.5" /> Tout marquer lu
              </button>
              {data.items.length > 0 && (
                <button data-testid="notifications-clear-all" onClick={clearAll}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Trash2 className="h-3.5 w-3.5" /> Vider
                </button>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground" data-testid="notifications-empty">Aucune notification.</div>
            )}
            {data.items.map((n) => (
              <div key={n.id} data-testid={`notification-item-${n.id}`}
                className={`group relative flex items-start gap-2 border-b border-border px-4 py-3 text-left transition-colors duration-200 last:border-b-0 hover:bg-accent ${!n.read ? "bg-primary/5" : ""}`}>
                <button onClick={() => openItem(n)} className="flex flex-1 items-start gap-2 text-left" data-testid={`notification-open-${n.id}`}>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  <div className={!n.read ? "pr-5" : "pl-4 pr-5"}>
                    <div className="text-xs font-semibold">{n.title}</div>
                    <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{n.message}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">{fmtDateTime(n.created_at)}</div>
                  </div>
                </button>
                <button data-testid={`notification-delete-${n.id}`} onClick={(e) => removeOne(e, n)} title="Supprimer"
                  className="absolute right-2 top-2 rounded-sm p-1 text-muted-foreground opacity-60 transition-all hover:bg-primary/10 hover:text-primary hover:opacity-100">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
